// ============================================================
// COMPONENTE: CALCULADORA DE PRESUPUESTO INTERACTIVA
// ============================================================
// El usuario añade sus propios gastos y ve el total en tiempo real.
// Datos guardados en backend (API). Organizado por categorías.
// ============================================================

import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useMediaQuery } from "../../hooks/UseMediaQuery";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import HotelIcon from "@mui/icons-material/Hotel";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import DirectionsTransitIcon from "@mui/icons-material/DirectionsTransit";
import LocalActivityIcon from "@mui/icons-material/LocalActivity";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import CategoryIcon from "@mui/icons-material/Category";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ScheduleIcon from "@mui/icons-material/Schedule";
import PieChartIcon from "@mui/icons-material/PieChart";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveIcon from "@mui/icons-material/Save";
import CheckIcon from "@mui/icons-material/Check";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

// Categorías disponibles con iconos MUI
const CATEGORIES = [
	{ id: "flights", Icon: FlightTakeoffIcon, label: "Vuelos" },
	{ id: "accommodation", Icon: HotelIcon, label: "Alojamiento" },
	{ id: "food", Icon: RestaurantIcon, label: "Comida" },
	{ id: "transport", Icon: DirectionsTransitIcon, label: "Transporte" },
	{ id: "activities", Icon: LocalActivityIcon, label: "Actividades" },
	{ id: "shopping", Icon: ShoppingBagIcon, label: "Compras" },
	{ id: "other", Icon: CategoryIcon, label: "Otros" },
];

const CATEGORY_MAP = Object.fromEntries(CATEGORIES.map((c) => [c.id, c]));

// Datos de referencia por defecto (se pueden pasar como prop)
export const defaultCityPrices = {
	cityName: "Mi viaje",
	currency: "€",
	accentColor: "#6366F1",
	accentColorLight: "rgba(99, 102, 241, 0.1)",
	priceExamples: [],
};

export default function BudgetCalculator({
	direction,
	cityPrices: propsCityPrices,
	projectId,
	api,
}) {
	const isMobile = useMediaQuery("(max-width: 600px)");
	const cityPrices = propsCityPrices || defaultCityPrices;
	const {
		accentColor,
		accentColorLight,
		cityName,
		currency = "€",
		dark: isDark = false,
		cardRadius = 16,
		cardShadow = "none",
		cardBorder,
		itemBorder,
	} = cityPrices;

	const cardStyle = {
		backgroundColor: isDark ? "#2d2d3a" : "#fff",
		borderRadius: cardRadius,
		padding: 16,
		border: cardBorder || (isDark ? "2px solid rgba(255,255,255,0.1)" : "2px solid #eee"),
		boxShadow: cardShadow,
	};
	const h4Style = {
		fontFamily: "'Poppins', sans-serif",
		fontSize: 14,
		fontWeight: 700,
		margin: 0,
		...(isDark && { color: "#e2e8f0" }),
	};

	// Estado principal
	const [items, setItems] = useState([]);
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [showAddForm, setShowAddForm] = useState(false);
	const [editingId, setEditingId] = useState(null);
	const [showTips, setShowTips] = useState(false);

	// Form state para nuevo gasto
	const [newConcept, setNewConcept] = useState("");
	const [newAmount, setNewAmount] = useState("");
	const [newCategory, setNewCategory] = useState("other");

	// Ref para auto-save con debounce
	const saveTimeoutRef = useRef(null);

	// Cargar items del backend
	useEffect(() => {
		if (!projectId || !api) return;
		setLoading(true);
		api
			.get(`/budget/${projectId}`)
			.then((data) => {
				setItems(Array.isArray(data) ? data : []);
			})
			.catch((err) => {
				console.error("Error cargando presupuesto:", err);
				// Intentar cargar desde localStorage como fallback
				const saved = localStorage.getItem(`budget-${projectId}`);
				if (saved) setItems(JSON.parse(saved));
			})
			.finally(() => setLoading(false));
	}, [projectId, api]);

	// Fallback: guardar en localStorage si no hay API
	useEffect(() => {
		if (projectId && items.length > 0) {
			localStorage.setItem(`budget-${projectId}`, JSON.stringify(items));
		}
	}, [items, projectId]);

	// Cálculos
	const totals = useMemo(() => {
		const byCategory = {};
		let total = 0;
		let paid = 0;
		let pending = 0;

		items.forEach((item) => {
			const cat = item.category || "other";
			if (!byCategory[cat]) byCategory[cat] = { total: 0, count: 0 };
			byCategory[cat].total += item.amount || 0;
			byCategory[cat].count += 1;
			total += item.amount || 0;
			if (item.isPaid) paid += item.amount || 0;
			else pending += item.amount || 0;
		});

		return { byCategory, total, paid, pending };
	}, [items]);

	// Añadir nuevo gasto
	const handleAddItem = useCallback(async () => {
		if (!newConcept.trim() && !newAmount) return;

		const concept = newConcept.trim() || "Gasto";
		const amount = parseFloat(newAmount) || 0;

		const tempItem = {
			id: `temp-${Date.now()}`,
			category: newCategory,
			concept,
			amount,
			currency,
			isPaid: false,
			order: items.length,
		};

		setItems((prev) => [...prev, tempItem]);
		setNewConcept("");
		setNewAmount("");
		setShowAddForm(false);

		// Guardar en backend
		if (projectId && api) {
			try {
				const saved = await api.post(`/budget/${projectId}`, {
					category: newCategory,
					concept,
					amount,
					currency,
				});
				// Reemplazar item temporal con el real
				setItems((prev) => prev.map((i) => (i.id === tempItem.id ? saved : i)));
			} catch (err) {
				console.error("Error guardando gasto:", err);
			}
		}
	}, [
		newConcept,
		newAmount,
		newCategory,
		currency,
		items.length,
		projectId,
		api,
	]);

	// Toggle pagado/pendiente
	const togglePaid = useCallback(
		async (id) => {
			setItems((prev) =>
				prev.map((i) => (i.id === id ? { ...i, isPaid: !i.isPaid } : i))
			);

			if (projectId && api && !id.startsWith("temp-")) {
				const item = items.find((i) => i.id === id);
				try {
					await api.put(`/budget/item/${id}`, { isPaid: !item?.isPaid });
				} catch (err) {
					console.error("Error actualizando gasto:", err);
				}
			}
		},
		[projectId, api, items]
	);

	// Editar item inline
	const handleEditSave = useCallback(
		async (id, updates) => {
			setItems((prev) =>
				prev.map((i) => (i.id === id ? { ...i, ...updates } : i))
			);
			setEditingId(null);

			if (projectId && api && !id.startsWith("temp-")) {
				// Debounce save
				clearTimeout(saveTimeoutRef.current);
				saveTimeoutRef.current = setTimeout(async () => {
					try {
						await api.put(`/budget/item/${id}`, updates);
					} catch (err) {
						console.error("Error actualizando gasto:", err);
					}
				}, 500);
			}
		},
		[projectId, api]
	);

	// Eliminar item
	const handleDelete = useCallback(
		async (id) => {
			setItems((prev) => prev.filter((i) => i.id !== id));

			if (projectId && api && !id.startsWith("temp-")) {
				try {
					await api.delete(`/budget/item/${id}`);
				} catch (err) {
					console.error("Error eliminando gasto:", err);
				}
			}
		},
		[projectId, api]
	);

	// Agrupar items por categoría
	const groupedItems = useMemo(() => {
		const groups = {};
		items.forEach((item) => {
			const cat = item.category || "other";
			if (!groups[cat]) groups[cat] = [];
			groups[cat].push(item);
		});
		return groups;
	}, [items]);

	const priceExamples = cityPrices?.priceExamples || [];

	return (
		<motion.div
			initial={{ opacity: 0, x: direction === "next" ? 100 : -100 }}
			animate={{ opacity: 1, x: 0 }}
			exit={{ opacity: 0, x: direction === "next" ? -100 : 100 }}
			transition={{ duration: 0.5 }}
			style={{ display: "flex", flexDirection: "column" }}
		>
			<div
				style={{
					display: "flex",
					flexDirection: "column",
					backgroundColor: isDark ? "#1a1a2e" : "#f7f7f7",
					color: isDark ? "#e2e8f0" : undefined,
					fontFamily: "'Poppins', sans-serif",
				}}
			>
				{/* Header */}
				<div
					style={{
						backgroundColor: accentColor,
						color: "#fff",
						textAlign: "center",
						padding: "15px 0",
						fontSize: "14px",
						fontWeight: 600,
						flexShrink: 0,
						position: "sticky",
						top: 0,
						zIndex: 99999,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						gap: 8,
					}}
				>
					<AccountBalanceWalletIcon sx={{ fontSize: 20 }} />
					Mi presupuesto {cityName ? `· ${cityName}` : ""}
				</div>

				<div
					style={{
					padding: "16px",
						display: "flex",
						flexDirection: "column",
						gap: 16,
					}}
				>
					{/* ── Resumen total ── */}
					<div
						style={{
							backgroundColor: accentColorLight,
							border: `3px solid ${accentColor}`,
							borderRadius: 20,
							padding: 20,
							textAlign: "center",
						}}
					>
						<p style={{ fontSize: 12, color: isDark ? "#94a3b8" : "#666", margin: 0 }}>
							Total presupuesto
						</p>
						<h2
							style={{
								fontSize: 36,
								fontWeight: 800,
								color: accentColor,
								margin: "8px 0",
							}}
						>
							{totals.total.toLocaleString("es-ES", {
								minimumFractionDigits: 2,
								maximumFractionDigits: 2,
							})}
							{currency}
						</h2>
						<div
							style={{
								display: "flex",
								justifyContent: "center",
								gap: 24,
								marginTop: 8,
							}}
						>
							<div style={{ textAlign: "center" }}>
								<p
									style={{
										margin: 0,
										fontSize: 18,
										fontWeight: 700,
										color: "#10B981",
									}}
								>
									{totals.paid.toLocaleString("es-ES", {
										minimumFractionDigits: 2,
									})}
									{currency}
								</p>
								<p
									style={{
										margin: 0,
										fontSize: 10,
										color: "#888",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										gap: 4,
									}}
								>
									<CheckCircleIcon sx={{ fontSize: 12 }} /> Pagado
								</p>
							</div>
						<div
							style={{
								width: 1,
								backgroundColor: isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.1)",
							}}
						/>
							<div style={{ textAlign: "center" }}>
								<p
									style={{
										margin: 0,
										fontSize: 18,
										fontWeight: 700,
										color: "#F59E0B",
									}}
								>
									{totals.pending.toLocaleString("es-ES", {
										minimumFractionDigits: 2,
									})}
									{currency}
								</p>
								<p
									style={{
										margin: 0,
										fontSize: 10,
										color: isDark ? "#94a3b8" : "#888",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										gap: 4,
									}}
								>
									<ScheduleIcon sx={{ fontSize: 12 }} /> Pendiente
								</p>
							</div>
						</div>
						{items.length > 0 && (
							<p
								style={{
									margin: "8px 0 0",
									fontSize: 11,
									color: isDark ? "#94a3b8" : "#888",
								}}
							>
								{items.length} gasto{items.length !== 1 ? "s" : ""} ·{" "}
								{items.filter((i) => i.isPaid).length} pagado
								{items.filter((i) => i.isPaid).length !== 1 ? "s" : ""}
							</p>
						)}
					</div>

					{/* ── Categorías con barras ── */}
					{items.length > 0 && (
						<div style={cardStyle}>
							<h4
								style={{
									...h4Style,
									color: accentColor,
									display: "flex",
									alignItems: "center",
									gap: 6,
								}}
							>
								<PieChartIcon sx={{ fontSize: 18 }} /> Por categoría
							</h4>
							<div
								style={{
									display: "flex",
									flexDirection: "column",
									gap: 10,
									marginTop: 12,
								}}
							>
								{CATEGORIES.filter((c) => totals.byCategory[c.id]).map(
									(cat) => {
										const data = totals.byCategory[cat.id];
										const pct =
											totals.total > 0 ? (data.total / totals.total) * 100 : 0;
										const CatIcon = cat.Icon;
										return (
											<div key={cat.id}>
												<div
													style={{
														display: "flex",
														justifyContent: "space-between",
														alignItems: "center",
														marginBottom: 4,
													}}
												>
													<span
														style={{
															fontSize: 13,
															fontWeight: 500,
															display: "flex",
															alignItems: "center",
															gap: 6,
														}}
													>
														<CatIcon
															sx={{ fontSize: 16, color: accentColor }}
														/>{" "}
														{cat.label}{" "}
														<span style={{ color: "#aaa", fontSize: 11 }}>
															({data.count})
														</span>
													</span>
													<span
														style={{
															fontSize: 13,
															fontWeight: 700,
															color: accentColor,
														}}
													>
														{data.total.toLocaleString("es-ES", {
															minimumFractionDigits: 2,
														})}
														{currency}
													</span>
												</div>
												<div
													style={{
														height: 6,
														backgroundColor: isDark ? "rgba(255,255,255,0.15)" : "#eee",
														borderRadius: 3,
														overflow: "hidden",
													}}
												>
													<div
														style={{
															height: "100%",
															width: `${pct}%`,
															backgroundColor: accentColor,
															borderRadius: 3,
															transition: "width 0.4s ease",
														}}
													/>
												</div>
											</div>
										);
									}
								)}
							</div>
						</div>
					)}

					{/* ── Lista de gastos por categoría ── */}
					{loading ? (
						<div
							style={{
								textAlign: "center",
								padding: 40,
								color: isDark ? "#94a3b8" : "#888",
							}}
						>
							Cargando presupuesto...
						</div>
					) : items.length === 0 ? (
						<div
							style={{
								...cardStyle,
								textAlign: "center",
								padding: 32,
							}}
						>
							<AccountBalanceWalletIcon
								sx={{ fontSize: 48, color: accentColor, mb: 1.5, opacity: 0.6 }}
							/>
							<p
								style={{
									fontWeight: 600,
									color: isDark ? "#e2e8f0" : "#333",
									marginBottom: 4,
								}}
							>
								Tu presupuesto está vacío
							</p>
							<p style={{ fontSize: 12, color: isDark ? "#94a3b8" : "#888", margin: 0 }}>
								Añade tus gastos reales para controlar el presupuesto de tu
								viaje
							</p>
						</div>
					) : (
						CATEGORIES.filter((c) => groupedItems[c.id]).map((cat) => {
							const CatIcon = cat.Icon;
							return (
								<div key={cat.id} style={cardStyle}>
									<h4
										style={{
											...h4Style,
											color: accentColor,
											marginBottom: 8,
											display: "flex",
											alignItems: "center",
											gap: 6,
										}}
									>
										<CatIcon sx={{ fontSize: 18 }} /> {cat.label}
									</h4>
									{groupedItems[cat.id].map((item) => (
										<BudgetItemRow
											key={item.id}
											item={item}
											currency={currency}
											accentColor={accentColor}
											isDark={isDark}
											itemBorder={itemBorder}
											isEditing={editingId === item.id}
											onEdit={() => setEditingId(item.id)}
											onSave={(updates) => handleEditSave(item.id, updates)}
											onTogglePaid={() => togglePaid(item.id)}
											onDelete={() => handleDelete(item.id)}
											onCancel={() => setEditingId(null)}
										/>
									))}
								</div>
							);
						})
					)}

					{/* ── Formulario nuevo gasto ── */}
					<AnimatePresence>
						{showAddForm && (
							<motion.div
								initial={{ opacity: 0, y: 20 }}
								animate={{ opacity: 1, y: 0 }}
								exit={{ opacity: 0, y: 20 }}
								style={{
									...cardStyle,
									border: `2px solid ${accentColor}`,
								}}
							>
								<h4
									style={{ ...h4Style, color: accentColor, marginBottom: 12 }}
								>
									Nuevo gasto
								</h4>

								{/* Selector de categoría */}
								<div
									style={{
										display: "flex",
										flexWrap: "wrap",
										gap: 6,
										marginBottom: 12,
									}}
								>
									{CATEGORIES.map((cat) => {
										const CatIcon = cat.Icon;
										return (
											<button
												key={cat.id}
												onClick={() => setNewCategory(cat.id)}
												style={{
													padding: "6px 10px",
													border: `1.5px solid ${
														newCategory === cat.id ? accentColor : "#ddd"
													}`,
													borderRadius: 8,
													backgroundColor:
														newCategory === cat.id ? accentColorLight : "#fff",
													cursor: "pointer",
													fontSize: 11,
													fontWeight: newCategory === cat.id ? 700 : 400,
													color: newCategory === cat.id ? accentColor : "#666",
													transition: "all 0.15s",
													display: "flex",
													alignItems: "center",
													gap: 4,
												}}
											>
												<CatIcon sx={{ fontSize: 14 }} /> {cat.label}
											</button>
										);
									})}
								</div>

								{/* Concepto */}
								<input
									type="text"
									placeholder="¿En qué gastas? (ej: Vuelo Madrid-Tokio)"
									value={newConcept}
									onChange={(e) => setNewConcept(e.target.value)}
									onKeyDown={(e) => e.key === "Enter" && handleAddItem()}
									style={{
										...inputStyle,
										borderColor: newConcept.trim() ? accentColor : "#ddd",
										marginBottom: 8,
									}}
								/>

								{/* Importe */}
								<div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
									<input
										type="number"
										inputMode="decimal"
										placeholder="Importe"
										value={newAmount}
										onChange={(e) => setNewAmount(e.target.value)}
										onKeyDown={(e) => e.key === "Enter" && handleAddItem()}
										style={{
											...inputStyle,
											flex: 1,
											borderColor: newAmount ? accentColor : "#ddd",
										}}
									/>
									<span
										style={{
											display: "flex",
											alignItems: "center",
											fontWeight: 700,
											fontSize: 18,
											color: accentColor,
											padding: "0 8px",
										}}
									>
										{currency}
									</span>
								</div>

								{/* Botones */}
								<div style={{ display: "flex", gap: 8 }}>
									<button
										onClick={handleAddItem}
										style={{
											flex: 1,
											padding: 12,
											border: "none",
											borderRadius: 12,
											backgroundColor: accentColor,
											color: "#fff",
											fontWeight: 700,
											fontSize: 14,
											cursor: "pointer",
											transition: "opacity 0.15s",
										}}
									>
										<CheckIcon sx={{ fontSize: 18, mr: 0.5 }} /> Añadir gasto
									</button>
									<button
										onClick={() => {
											setShowAddForm(false);
											setNewConcept("");
											setNewAmount("");
										}}
										style={{
											padding: "12px 16px",
											border: "2px solid #ddd",
											borderRadius: 12,
											backgroundColor: "#fff",
											color: "#888",
											fontWeight: 600,
											fontSize: 14,
											cursor: "pointer",
											display: "flex",
											alignItems: "center",
										}}
									>
										<CloseIcon sx={{ fontSize: 18 }} />
									</button>
								</div>
							</motion.div>
						)}
					</AnimatePresence>

					{/* Botón añadir */}
					{!showAddForm && (
						<button
							onClick={() => setShowAddForm(true)}
							style={{
								padding: 14,
								border: `2px dashed ${accentColor}`,
								borderRadius: 16,
								backgroundColor: accentColorLight,
								color: accentColor,
								fontWeight: 700,
								fontSize: 14,
								cursor: "pointer",
								transition: "all 0.2s",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								gap: 8,
							}}
						>
							<AddIcon sx={{ fontSize: 22 }} /> Añadir gasto
						</button>
					)}

					{/* ── Precios de referencia (si los hay) ── */}
					{priceExamples.length > 0 && (
						<div style={cardStyle}>
							<button
								onClick={() => setShowTips(!showTips)}
								style={{
									display: "flex",
									alignItems: "center",
									justifyContent: "space-between",
									width: "100%",
									border: "none",
									backgroundColor: "transparent",
									cursor: "pointer",
									padding: 0,
								}}
							>
								<h4
									style={{
										...h4Style,
										color: accentColor,
										display: "flex",
										alignItems: "center",
										gap: 6,
									}}
								>
									<LightbulbIcon sx={{ fontSize: 18 }} /> Precios de referencia
									en {cityName}
								</h4>
								<ExpandMoreIcon
									sx={{
										fontSize: 20,
										color: "#888",
										transform: showTips ? "rotate(180deg)" : "rotate(0deg)",
										transition: "transform 0.2s",
									}}
								/>
							</button>
							{showTips && (
								<div
									style={{
										display: "grid",
										gridTemplateColumns: "1fr 1fr",
										gap: 8,
										marginTop: 12,
									}}
								>
									{priceExamples.map((ex, i) => (
										<div
											key={i}
											style={{
												backgroundColor: "#f9f9f9",
												borderRadius: 8,
												padding: "8px 10px",
												fontSize: 11,
											}}
										>
											<p
												style={{
													margin: 0,
													color: "#666",
												}}
											>
												{ex.item}
											</p>
											<p
												style={{
													margin: 0,
													fontWeight: 700,
													color: accentColor,
												}}
											>
												{ex.price}
											</p>
										</div>
									))}
								</div>
							)}
						</div>
					)}

					<p
						style={{
							fontSize: 10,
							color: "#999",
							textAlign: "center",
							padding: "0 16px",
							lineHeight: 1.5,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							gap: 6,
						}}
					>
						<SaveIcon sx={{ fontSize: 14 }} /> Tus gastos se guardan
						automáticamente
					</p>
				</div>
			</div>
		</motion.div>
	);
}

// ── Item Row (editable inline) ──
function BudgetItemRow({
	item,
	currency,
	accentColor,
	isDark = false,
	itemBorder,
	isEditing,
	onEdit,
	onSave,
	onTogglePaid,
	onDelete,
	onCancel,
}) {
	const [concept, setConcept] = useState(item.concept);
	const [amount, setAmount] = useState(String(item.amount || ""));
	const dimColor = isDark ? "#64748B" : "#aaa";
	const baseText = isDark ? "#e2e8f0" : "#333";
	const iconDim = isDark ? "#4a5568" : "#ccc";
	const rowBorder = itemBorder || (isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid #f0f0f0");

	useEffect(() => {
		setConcept(item.concept);
		setAmount(String(item.amount || ""));
	}, [item, isEditing]);

	const handleSave = () => {
		onSave({
			concept: concept.trim() || item.concept,
			amount: parseFloat(amount) || item.amount,
		});
	};

	const darkInputOverride = isDark
		? {
				backgroundColor: "#1a1a2e",
				color: "#e2e8f0",
				borderColor: "rgba(255,255,255,0.15)",
			}
		: {};

	if (isEditing) {
		return (
			<div
				style={{
					padding: "10px 0",
					borderBottom: rowBorder,
					display: "flex",
					flexDirection: "column",
					gap: 8,
				}}
			>
				<input
					type="text"
					value={concept}
					onChange={(e) => setConcept(e.target.value)}
					onKeyDown={(e) => e.key === "Enter" && handleSave()}
					autoFocus
					style={{ ...inputStyle, fontSize: 13, padding: "8px 10px", ...darkInputOverride }}
				/>
				<div style={{ display: "flex", gap: 8, alignItems: "center" }}>
					<input
						type="number"
						inputMode="decimal"
						value={amount}
						onChange={(e) => setAmount(e.target.value)}
						onKeyDown={(e) => e.key === "Enter" && handleSave()}
						style={{
							...inputStyle,
							flex: 1,
							fontSize: 13,
							padding: "8px 10px",
							...darkInputOverride,
						}}
					/>
					<button
						onClick={handleSave}
						style={{
							padding: "8px 12px",
							border: "none",
							borderRadius: 8,
							backgroundColor: accentColor,
							color: "#fff",
							fontWeight: 600,
							fontSize: 12,
							cursor: "pointer",
							display: "flex",
							alignItems: "center",
						}}
					>
						<CheckIcon sx={{ fontSize: 18 }} />
					</button>
					<button
						onClick={onCancel}
						style={{
							padding: "8px 12px",
							border: isDark ? "1px solid rgba(255,255,255,0.15)" : "1px solid #ddd",
							borderRadius: 8,
							backgroundColor: isDark ? "#2d2d3a" : "#fff",
							color: isDark ? "#94a3b8" : "#888",
							fontSize: 12,
							cursor: "pointer",
							display: "flex",
							alignItems: "center",
						}}
					>
						<CloseIcon sx={{ fontSize: 18 }} />
					</button>
				</div>
			</div>
		);
	}

	return (
		<div
			style={{
				display: "flex",
				alignItems: "center",
				justifyContent: "space-between",
				padding: "10px 0",
				borderBottom: rowBorder,
				gap: 8,
			}}
		>
			<button
				onClick={onTogglePaid}
				style={{
					width: 24,
					height: 24,
					borderRadius: "50%",
					border: `2px solid ${item.isPaid ? "#10B981" : (isDark ? "#4a5568" : "#ccc")}`,
					backgroundColor: item.isPaid ? "#10B981" : "transparent",
					cursor: "pointer",
					flexShrink: 0,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					transition: "all 0.2s",
					padding: 0,
				}}
			>
				{item.isPaid && <CheckIcon sx={{ color: "#fff", fontSize: 14 }} />}
			</button>

			<div
				onClick={onEdit}
				style={{
					flex: 1,
					minWidth: 0,
					cursor: "pointer",
				}}
			>
				<p
					style={{
						margin: 0,
						fontSize: 13,
						fontWeight: 500,
						color: item.isPaid ? dimColor : baseText,
						textDecoration: item.isPaid ? "line-through" : "none",
						whiteSpace: "nowrap",
						overflow: "hidden",
						textOverflow: "ellipsis",
					}}
				>
					{item.concept}
				</p>
			</div>

			<span
				style={{
					fontSize: 14,
					fontWeight: 700,
					color: item.isPaid ? "#10B981" : accentColor,
					flexShrink: 0,
					whiteSpace: "nowrap",
				}}
			>
				{(item.amount || 0).toLocaleString("es-ES", {
					minimumFractionDigits: 2,
				})}
				{currency}
			</span>

			<button
				onClick={onDelete}
				style={{
					width: 28,
					height: 28,
					borderRadius: 8,
					border: "none",
					backgroundColor: "transparent",
					color: iconDim,
					cursor: "pointer",
					fontSize: 14,
					flexShrink: 0,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					transition: "color 0.15s",
				}}
				onMouseEnter={(e) => (e.currentTarget.style.color = "#EF4444")}
				onMouseLeave={(e) => (e.currentTarget.style.color = iconDim)}
			>
				<DeleteOutlineIcon sx={{ fontSize: 18 }} />
			</button>
		</div>
	);
}

// Estilo compartido para inputs (cardStyle y h4Style son dinámicos según tema, dentro del componente)
const inputStyle = {
	width: "100%",
	padding: "10px 12px",
	border: "2px solid #ddd",
	borderRadius: 10,
	fontSize: 14,
	fontFamily: "'Poppins', sans-serif",
	outline: "none",
	transition: "border-color 0.15s",
	boxSizing: "border-box",
};
